// Route Configuration for Dental Clinic Management System
// This file defines all routes and their access requirements

export const ROUTE_CONFIG = {
  // Public Routes - No Authentication Required
  PUBLIC: {
    '/': { requireAuth: false, allowedRoles: [] },
    '/services': { requireAuth: false, allowedRoles: [] },
    '/sign-in': { requireAuth: false, allowedRoles: [] },
    '/sign-up-patient': { requireAuth: false, allowedRoles: [] },
    '/sign-up-dentistowner': { requireAuth: false, allowedRoles: [] },
    '/forgot-password': { requireAuth: false, allowedRoles: [] },
    '/verify-email': { requireAuth: false, allowedRoles: [] },
  },

  // Personnel Sign-up Routes - Requires valid pendingPersonnel invitation
  PERSONNEL_SIGNUP: {
    '/sign-up-dentistowner': { 
      requireAuth: false, 
      allowedRoles: [], 
      requiresInvitation: true, 
      personnelType: 'DentistOwner' 
    },
    '/sign-up-associatedentist': { 
      requireAuth: false, 
      allowedRoles: [], 
      requiresInvitation: true, 
      personnelType: 'AssociateDentist' 
    },
    '/sign-up-clinicstaff': { 
      requireAuth: false, 
      allowedRoles: [], 
      requiresInvitation: true, 
      personnelType: 'ClinicStaff' 
    },
  },

  // Patient-Only Routes
  PATIENT: {
    '/dashboard-patient': { requireAuth: true, allowedRoles: ['Patient'] },
    '/patient-appointment-booking': { requireAuth: true, allowedRoles: ['Patient'] },
    '/patient-appointment-status': { requireAuth: true, allowedRoles: ['Patient'] },
    '/treatment-history': { requireAuth: true, allowedRoles: ['Patient'] },
    '/settings': { requireAuth: true, allowedRoles: ['Patient'] },
  },

  // Dentist Owner Routes - Full Administrative Access
  DENTIST_OWNER: {
    '/dashboard-dentistowner': { requireAuth: true, allowedRoles: ['DentistOwner'] },
    '/manage-personnel': { requireAuth: true, allowedRoles: ['DentistOwner'] },
    '/manage-services': { requireAuth: true, allowedRoles: ['DentistOwner'] },
  },

  // Associate Dentist Routes
  ASSOCIATE_DENTIST: {
    '/dashboard-associatedentist': { requireAuth: true, allowedRoles: ['AssociateDentist'] },
  },

  // Clinic Staff Routes
  CLINIC_STAFF: {
    '/dashboard-clinicstaff': { requireAuth: true, allowedRoles: ['ClinicStaff'] },
    '/settings-personnel': { requireAuth: true, allowedRoles: ['ClinicStaff'] },
  },

  // Common Routes - Accessible to All Authenticated Users
  COMMON: {
    '/settings': { requireAuth: true, allowedRoles: ['DentistOwner', 'AssociateDentist', 'ClinicStaff', 'Patient'] },
  },

  // Staff-Only Routes (Dentists + Clinic Staff)
  STAFF: {
    '/manage-appointment': { requireAuth: true, allowedRoles: ['DentistOwner', 'AssociateDentist', 'ClinicStaff'] },
    '/patient-record': { requireAuth: true, allowedRoles: ['DentistOwner', 'AssociateDentist', 'ClinicStaff'] },
    '/inventory': { requireAuth: true, allowedRoles: ['DentistOwner', 'AssociateDentist', 'ClinicStaff'] },
  },

  // Dentist-Only Routes (Owner + Associate)
  DENTISTS: {
    '/analytics': { requireAuth: true, allowedRoles: ['DentistOwner', 'AssociateDentist'] },
  },

  // Error Pages
  ERROR: {
    '/unauthorized': { requireAuth: false, allowedRoles: [] },
    '*': { requireAuth: false, allowedRoles: [] },
  },
};

// Helper function to get route configuration
export const getRouteConfig = (path) => {
  // Check each category for the route
  for (const category of Object.values(ROUTE_CONFIG)) {
    if (category[path]) {
      return category[path];
    }
  }
  
  // Default configuration for unknown routes
  return { requireAuth: true, allowedRoles: [] };
};

// Helper function to check if user has access to a route
export const hasRouteAccess = (userRole, path) => {
  const config = getRouteConfig(path);
  
  if (!config.requireAuth) {
    return true; // Public route
  }
  
  if (!userRole) {
    return false; // No role, no access
  }
  
  return config.allowedRoles.includes(userRole);
};

// Helper function to check if route requires personnel invitation
export const requiresPersonnelInvitation = (path) => {
  const config = getRouteConfig(path);
  return config.requiresInvitation === true;
};

// Helper function to get personnel type for invitation verification
export const getPersonnelTypeForRoute = (path) => {
  const config = getRouteConfig(path);
  return config.personnelType || null;
};

// Route categories for easy reference
export const ROUTE_CATEGORIES = {
  PUBLIC: 'Public Access',
  PERSONNEL_SIGNUP: 'Personnel Sign-up (Invitation Required)',
  PATIENT: 'Patient Only',
  DENTIST_OWNER: 'Dentist Owner Only',
  ASSOCIATE_DENTIST: 'Associate Dentist Only',
  CLINIC_STAFF: 'Clinic Staff Only',
  STAFF: 'Staff Access (Dentists + Staff)',
  DENTISTS: 'Dentists Only (Owner + Associate)',
  ERROR: 'Error Pages',
};

// Navigation menu structure based on user roles
export const getNavigationMenu = (userRole) => {
  const menu = {
    Patient: [
      { path: '/dashboard-patient', label: 'Dashboard', icon: '🏠' },
      { path: '/patient-appointment-booking', label: 'Book Appointment', icon: '📅' },
      { path: '/patient-appointment-status', label: 'Appointment Status', icon: '📋' },
      { path: '/treatment-history', label: 'Treatment History', icon: '📊' },
      { path: '/settings', label: 'Settings', icon: '⚙️' },
    ],
    DentistOwner: [
      { path: '/dashboard-dentistowner', label: 'Dashboard', icon: '🏠' },
      { path: '/manage-personnel', label: 'Manage Personnel', icon: '👥' },
      { path: '/manage-appointment', label: 'Manage Appointments', icon: '📅' },
      { path: '/patient-record', label: 'Patient Records', icon: '📋' },
      { path: '/inventory', label: 'Inventory', icon: '📦' },
      { path: '/analytics', label: 'Analytics', icon: '📊' },
      { path: '/settings', label: 'Settings', icon: '⚙️' },
    ],
    AssociateDentist: [
      { path: '/dashboard-associatedentist', label: 'Dashboard', icon: '🏠' },
      { path: '/manage-appointment', label: 'Manage Appointments', icon: '📅' },
      { path: '/patient-record', label: 'Patient Records', icon: '📋' },
      { path: '/inventory', label: 'Inventory', icon: '📦' },
      { path: '/analytics', label: 'Analytics', icon: '📊' },
      { path: '/settings', label: 'Settings', icon: '⚙️' },
    ],
    ClinicStaff: [
      { path: '/dashboard-clinicstaff', label: 'Dashboard', icon: '🏠' },
      { path: '/manage-appointment', label: 'Manage Appointments', icon: '📅' },
      { path: '/patient-record', label: 'Patient Records', icon: '📋' },
      { path: '/inventory', label: 'Inventory', icon: '📦' },
      { path: '/settings', label: 'Settings', icon: '⚙️' },
    ],
  };

  return menu[userRole] || [];
};

// Security audit function
export const auditRouteSecurity = () => {
  const issues = [];
  
  // Check for routes that might be too permissive
  Object.entries(ROUTE_CONFIG).forEach(([category, routes]) => {
    Object.entries(routes).forEach(([path, config]) => {
      if (path.includes('admin') && config.allowedRoles.length > 1) {
        issues.push(`Warning: Admin route ${path} allows multiple roles`);
      }
      
      if (path.includes('patient') && !config.allowedRoles.includes('Patient')) {
        issues.push(`Warning: Patient route ${path} doesn't allow Patient role`);
      }

      // Check if personnel sign-up routes are properly secured
      if (path.includes('sign-up') && (path.includes('associatedentist') || path.includes('clinicstaff'))) {
        if (!config.requiresInvitation) {
          issues.push(`Warning: Personnel sign-up route ${path} is not properly secured`);
        }
      }
    });
  });
  
  return issues;
};
